import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { ApplicationNote, NoteData } from "shared/app/Notes/types";
import addressApi from "api/landlord/address";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";
import { useMemo, useState } from "react";

type NotesListResponse = {
  object: "list";
  items: ApplicationNote[];
  current_page: number;
  page_count: number;
  total_count: number;
  has_more: boolean;
  extra?: {
    mentioned_notes_count?: number;
    total_notes_count?: number;
  };
};

const notesQueryKey = (
  applicationId: number | string,
  queryParams?: Record<string, string | string[] | boolean>
) => ["NOTES", applicationId, queryParams];

export enum ShowingNotesType {
  ALL = "ALL",
  MENTIONS = "MENTIONS"
}

export const useNotesController = (
  applicationId: string,
  onNoteChange?: () => void
) => {
  const [showingNotesType, setShowingNotesType] = useState<ShowingNotesType>(
    ShowingNotesType.ALL
  );

  const mentionedOnly = showingNotesType === ShowingNotesType.MENTIONS;

  const notesMeta = useInfiniteQuery<NotesListResponse>({
    queryKey: notesQueryKey(applicationId, { mentioned_only: mentionedOnly }),
    queryFn: ({ pageParam }) =>
      instance
        .get(`portal/v1/applications/${applicationId}/notes`, {
          params: {
            per_page: 20,
            page: pageParam,
            mentioned_only: mentionedOnly
          }
        })
        .then(({ data }) => data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.current_page + 1 : undefined,
    enabled: Boolean(applicationId && applicationId !== "undefined")
  });

  const addNoteMeta = useMutation({
    mutationFn: (newNote: NoteData) =>
      addressApi.addApplicationNote(Number(applicationId), newNote),
    mutationKey: [...notesQueryKey(applicationId), "CREATE"],
    onSuccess: () => {
      notesMeta.refetch();
      onNoteChange?.();
    },
    onError: (err) => toast.error(getErrorMessage(err))
  });

  const editNoteMeta = useMutation({
    mutationFn: ({ noteId, data }: { noteId: number; data: NoteData }) =>
      addressApi.editApplicationNote(Number(applicationId), noteId, data),
    mutationKey: [...notesQueryKey(applicationId), "UPDATE"],
    onSuccess: () => {
      notesMeta.refetch();
      onNoteChange?.();
    },
    onError: (err) => toast.error(getErrorMessage(err))
  });

  const deleteNoteMeta = useMutation({
    mutationFn: (noteId: number) =>
      addressApi.deleteApplicationNote(Number(applicationId), noteId),
    mutationKey: [...notesQueryKey(applicationId), "DELETE"],
    onSuccess: () => {
      notesMeta.refetch();
      onNoteChange?.();
    },
    onError: (err) => toast.error(getErrorMessage(err))
  });

  const changeShowingNotesType = (type: ShowingNotesType) => {
    setShowingNotesType(type);
  };

  const allNotes = useMemo(() => {
    return notesMeta.data?.pages.flatMap((page) => page.items) ?? [];
  }, [notesMeta.data]);

  const mentionNotesCount = useMemo(() => {
    return notesMeta.data?.pages[0]?.extra?.mentioned_notes_count ?? 0;
  }, [notesMeta.data]);

  const totalNotesCount = useMemo(() => {
    return notesMeta.data?.pages[0]?.extra?.total_notes_count ?? 0;
  }, [notesMeta.data]);

  return {
    notesMeta,
    showingNotes: allNotes,
    deleteNoteMeta,
    editNoteMeta,
    addNoteMeta,
    mentionNotesCount,
    totalNotesCount,
    showingNotesType,
    changeShowingNotesType
  };
};
